import React from 'react';
import './ProgressBar.css';

interface ProgressBarProps {
  currentStep: 1 | 2 | 3;
  // Optional props for mobile version
  totalSteps?: number;
  nextStepLabel?: string;
  mobileTitle?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ 
  currentStep,
  totalSteps = 3,
  nextStepLabel = getDefaultNextLabel(currentStep),
  mobileTitle = "Create a Book"
}) => {
  // Calculate progress percentage for mobile circular progress
  const progressPercentage = (currentStep / totalSteps) * 100;
  const circumference = 2 * Math.PI * 45; // radius of 45
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  return (
    <>
      {/* Desktop Progress Bar - Original Design */}
      <div className="progress-bar-container desktop-progress">
        <div className="progress-bar-wrapper">
          <svg width="602" height="36" viewBox="0 0 602 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Line between step 1 and 2 - orange if step 2 is active or completed */}
            <path 
              d="M34 18H284" 
              stroke={currentStep >= 2 ? "#F4A940" : "#E7E7EB"} 
              strokeWidth="2"
            />
            {/* Line between step 2 and 3 - orange if step 3 is active or completed */}
            <path 
              d="M318 18H568" 
              stroke={currentStep >= 3 ? "#F4A940" : "#E7E7EB"} 
              strokeWidth="2"
            />
            
            {/* Circle backgrounds - using filter for shadow effect but only showing them when needed */}
            <g filter="url(#filter0_i_1_98485)" opacity="0.5">
              <path d="M584 0C593.941 0 602 8.05887 602 18C602 27.9411 593.941 36 584 36C576.933 36 570.818 31.9267 567.872 26H317.128C314.182 31.9267 308.067 36 301 36C293.933 36 287.818 31.9267 284.872 26H34.1279C31.1824 31.9267 25.0673 36 18 36C8.05887 36 0 27.9411 0 18C0 8.05887 8.05887 0 18 0C25.0673 0 31.1824 4.07335 34.1279 10H284.872C287.818 4.07335 293.933 0 301 0C308.067 0 314.182 4.07335 317.128 10H567.872C570.818 4.07335 576.933 0 584 0Z" fill="#E7E7EB"/>
            </g>
            <defs>
              <filter id="filter0_i_1_98485" x="0" y="0" width="602" height="40" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="4"/>
                <feGaussianBlur stdDeviation="2"/>
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0"/>
                <feBlend mode="normal" in2="shape" result="effect1_innerShadow_1_98485"/>
              </filter>
            </defs>
          </svg>
        </div>
        
        {/* Step 1 */}
        <div className="step-circle step-1-circle">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="13" fill={currentStep >= 1 ? "#F4A940" : "white"} stroke={currentStep >= 1 ? "#F4A940" : "transparent"} strokeWidth="2"/>
          </svg>
          {currentStep > 1 ? (
            <div className="checkmark">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          ) : (
            <div className={`step-number ${currentStep >= 1 ? "step-number-active" : ""}`}>1</div>
          )}
        </div>
        
        {/* Step 2 */}
        <div className="step-circle step-2-circle">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="13" fill={currentStep >= 2 ? "#F4A940" : "white"} stroke={currentStep >= 2 ? "#F4A940" : "transparent"} strokeWidth="2"/>
          </svg>
          {currentStep > 2 ? (
            <div className="checkmark">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          ) : (
            <div className={`step-number ${currentStep >= 2 ? "step-number-active" : ""}`}>2</div>
          )}
        </div>
        
        {/* Step 3 */}
        <div className="step-circle step-3-circle">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="13" fill={currentStep >= 3 ? "#F4A940" : "white"} stroke={currentStep >= 3 ? "#F4A940" : "transparent"} strokeWidth="2"/>
          </svg>
          <div className={`step-number ${currentStep >= 3 ? "step-number-active" : ""}`}>3</div>
        </div>
        
        {/* Step Labels */}
        <div className={`step-label step-1-label ${currentStep >= 1 ? "step-label-active" : ""}`}>Story outline<br/>& Add a character</div>
        <div className={`step-label step-2-label ${currentStep >= 2 ? "step-label-active" : ""}`}>Add Story Preferences</div>
        <div className={`step-label step-3-label ${currentStep >= 3 ? "step-label-active" : ""}`}>Review & Submit</div>
      </div>

      {/* Mobile Progress Bar */}
      <div className="mobile-progress">
        <div className="mobile-progress-header">
          <div className="progress-circle-container">
            <svg className="progress-circle" width="100" height="100" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#E7E7EB"
                strokeWidth="6"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#E13EED"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 50 50)"
                className="progress-circle-fill"
              />
            </svg>
            <div className="progress-text">
              <span className="progress-current">{currentStep}</span>
              <span className="progress-divider"> of </span>
              <span className="progress-total">{totalSteps}</span>
            </div>
          </div>
          
          <div className="mobile-progress-info">
            <h2 className="progress-title">{mobileTitle}</h2>
            {nextStepLabel && (
              <p className="progress-next">Next: {nextStepLabel}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Helper function to get default next step label based on current step
function getDefaultNextLabel(currentStep: number): string {
  switch (currentStep) {
    case 1:
      return "Add Story Preferences";
    case 2:
      return "Review & Submit";
    case 3:
      return ""; // No next step for final step
    default:
      return "Next Step";
  }
}

export default ProgressBar; 